import { useCallback, useEffect, useState } from 'react';
import { useIsAdmin } from '../api/auth';

/**
 * Choosing a camera for a line.
 *
 * The page is built around one distinction that the API also enforces: the boxes
 * at the top are PHYSICS and the buttons beside the results are PREFERENCE.
 *
 * A camera that cannot put three pixels on the defect, or cannot read out fast
 * enough for the belt, is not a worse option — it is not an option, and no
 * ordering brings it back. Which of the survivors you want is nobody's business
 * but yours, so every sort is offered rather than one being chosen for you.
 *
 * The two result lists are separate for the same reason. Omron quotes on
 * request, which is not the same as expensive and not the same as cheap. Ranking
 * an unknown price against a known one means inventing a comparison, and the
 * first version of this did exactly that — it put a €279 camera below seven
 * whose price nobody had.
 */

interface Camera {
  vendor: string;
  model: string;
  family: string;
  scan: 'area' | 'line';
  widthPx: number;
  heightPx: number | null;
  speed: number;
  speedUnit: 'fps' | 'kHz';
  interface: string;
  colour: 'mono' | 'colour';
  shutter: 'global' | 'rolling' | null;
  shutterUnverified: boolean;
  spectrum: string;
  sensor: string | null;
  pixelSizeUm: number | null;
  ingressProtection: string | null;
  mount: string | null;
  driver: string;
  mmPerPixel: number;
  pixelsPerFeature: number;
  requiredSpeed: number;
  headroom: number;
  needsFrameGrabber: boolean;
  inCatalogue: boolean;
  listPrice: number | null;
  currency: string;
  priceRetrievedAt: string | null;
}

interface Result {
  considered: number;
  matched: number;
  returned: number;
  sort: string;
  onRequestCount: number;
  // The same shape as the priced cameras — an unpriced one is adoptable too, it
  // just carries no price — so it renders in the same table and can be added.
  onRequest: Camera[];
  cameras: Camera[];
}

interface Vendor {
  name: string;
  models: number;
  source: string;
  caveat: string | null;
}

const SORTS = [
  { key: 'best', label: 'Best fit', hint: 'Cheapest to buy and install' },
  { key: 'price', label: 'Price', hint: 'List price, low to high' },
  { key: 'resolution', label: 'Resolution', hint: 'Most pixels across the belt' },
  { key: 'speed', label: 'Speed', hint: 'Fastest readout' },
  { key: 'headroom', label: 'Headroom', hint: 'Most margin over what the line needs' },
] as const;

const cell: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', whiteSpace: 'nowrap' };
const num: React.CSSProperties = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

export function CameraPickerView() {
  const isAdmin = useIsAdmin();

  // The line. Defaults describe a small conveyor rather than being empty: an
  // empty form gives no results, and no results looks like a broken page.
  const [fieldOfViewMm, setFieldOfViewMm] = useState('300');
  const [smallestFeatureMm, setSmallestFeatureMm] = useState('1');
  const [beltSpeedMmS, setBeltSpeedMmS] = useState('500');
  const [pitchMm, setPitchMm] = useState('120');

  const [needsColour, setNeedsColour] = useState(false);
  const [washdown, setWashdown] = useState(false);
  const [allowRollingShutter, setAllowRollingShutter] = useState(false);
  const [spectrum, setSpectrum] = useState('visible');
  const [vendors, setVendors] = useState<string[]>([]);
  const [minHeadroom, setMinHeadroom] = useState('2');
  const [sort, setSort] = useState<string>('best');

  const [vendorList, setVendorList] = useState<Vendor[]>([]);
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/suppliers/cameras/vendors')
      .then((r) => (r.ok ? r.json() : []))
      .then(setVendorList)
      .catch(() => undefined);
  }, []);

  const search = useCallback(async () => {
    setBusy(true);
    setError(null);
    const params = new URLSearchParams({
      fieldOfViewMm,
      smallestFeatureMm,
      beltSpeedMmS,
      pitchMm,
      minHeadroom,
      sort,
      limit: '25',
    });
    if (needsColour) params.set('needsColour', 'true');
    if (washdown) params.set('washdown', 'true');
    if (allowRollingShutter) params.set('allowRollingShutter', 'true');
    if (spectrum !== 'visible') params.set('spectrum', spectrum);
    for (const v of vendors) params.append('vendors', v);
    try {
      const res = await fetch(`/api/suppliers/cameras?${params}`);
      if (!res.ok) throw new Error((await res.json()).message ?? 'Search failed');
      setResult(await res.json());
    } catch (e) {
      setError((e as Error).message);
      setResult(null);
    } finally {
      setBusy(false);
    }
  }, [
    fieldOfViewMm,
    smallestFeatureMm,
    beltSpeedMmS,
    pitchMm,
    minHeadroom,
    sort,
    needsColour,
    washdown,
    allowRollingShutter,
    spectrum,
    vendors,
  ]);

  useEffect(() => {
    void search();
  }, [search]);

  const adopt = async (camera: Camera) => {
    setError(null);
    try {
      const res = await fetch(
        `/api/suppliers/cameras/${encodeURIComponent(camera.vendor)}/${encodeURIComponent(camera.model)}/adopt`,
        { method: 'POST' },
      );
      const body = await res.json();
      if (!res.ok) throw new Error(body.message ?? 'Could not adopt');
      setNotice(
        body.created
          ? `${camera.model} added to the catalogue — set your own price there.`
          : `${camera.model} was already in the catalogue.`,
      );
      void search();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div>
      <h1>Camera selection</h1>
      <p className="muted" style={{ marginTop: -8, maxWidth: 680 }}>
        Describe the line and see which cameras can actually inspect it. Everything
        listed can do the job — the ordering is yours to choose.
      </p>

      <div className="panel" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field label="Field of view" unit="mm" value={fieldOfViewMm} onChange={setFieldOfViewMm} />
          <Field
            label="Smallest defect"
            unit="mm"
            value={smallestFeatureMm}
            onChange={setSmallestFeatureMm}
            hint="Three pixels must land on this"
          />
          <Field label="Belt speed" unit="mm/s" value={beltSpeedMmS} onChange={setBeltSpeedMmS} />
          <Field
            label="Part pitch"
            unit="mm"
            value={pitchMm}
            onChange={setPitchMm}
            hint="Length plus gap"
          />
          <Field
            label="Min. margin"
            unit="×"
            value={minHeadroom}
            onChange={setMinHeadroom}
            hint="Lines rarely run at nominal"
          />
        </div>

        <div
          style={{
            display: 'flex',
            gap: 20,
            flexWrap: 'wrap',
            marginTop: 16,
            paddingTop: 14,
            borderTop: '1px solid var(--border)',
            alignItems: 'center',
          }}
        >
          <Check
            label="Colour needed"
            checked={needsColour}
            onChange={setNeedsColour}
            emphasis="var(--warn)"
          />
          <Check
            label="Washdown (sealed housing)"
            checked={washdown}
            onChange={setWashdown}
            emphasis="var(--warn)"
          />
          <Check
            label="Allow rolling shutter"
            checked={allowRollingShutter}
            onChange={setAllowRollingShutter}
            title="Only for an indexed line where the part is stationary when photographed. On a moving belt a rolling shutter skews the part."
            emphasis="var(--warn)"
          />
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--warn)' }}>Spectrum</span>
            <select value={spectrum} onChange={(e) => setSpectrum(e.target.value)}>
              <option value="visible">Visible</option>
              <option value="SWIR">SWIR</option>
              <option value="UV">UV</option>
            </select>
          </label>
          {vendorList.map((v) => (
            <Check
              key={v.name}
              label={`${v.name} (${v.models})`}
              checked={vendors.includes(v.name)}
              onChange={(on) =>
                setVendors((cur) => (on ? [...cur, v.name] : cur.filter((x) => x !== v.name)))
              }
              title={v.caveat ?? undefined}
              emphasis="var(--accent)"
            />
          ))}
        </div>
      </div>

      {error && (
        <div className="panel" style={{ color: 'var(--bad)', marginBottom: 16 }}>{error}</div>
      )}
      {notice && (
        <div className="panel" style={{ color: 'var(--good)', marginBottom: 16 }}>{notice}</div>
      )}

      <div className="panel">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div className="muted" style={{ fontSize: 13 }}>
            {busy
              ? 'Searching…'
              : result
                ? `${result.matched} of ${result.considered} cameras can do this${
                    result.matched > result.returned + result.onRequestCount
                      ? `, showing ${result.returned}`
                      : ''
                  }`
                : ''}
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <span className="muted" style={{ fontSize: 13, marginRight: 4 }}>Order by</span>
            {SORTS.map((s) => (
              <button
                key={s.key}
                className={`btn sm${sort === s.key ? ' primary' : ''}`}
                title={s.hint}
                onClick={() => setSort(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {result && result.cameras.length === 0 && result.onRequestCount === 0 && (
          <div className="muted" style={{ padding: '20px 0' }}>
            Nothing fits. The usual cause is a defect too small for the field of view —{' '}
            {fieldOfViewMm} mm across needs about{' '}
            {Math.round((Number(fieldOfViewMm) / Number(smallestFeatureMm)) * 3).toLocaleString(
              'nl-NL',
            )}{' '}
            pixels to put three on a {smallestFeatureMm} mm feature. Narrow the view, or inspect in
            two lanes.
          </div>
        )}

        {result && result.cameras.length > 0 && (
          <CameraTable rows={result.cameras} isAdmin={isAdmin} onAdopt={adopt} />
        )}

        {result && result.onRequestCount > 0 && (
          <div style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              Price on request — {result.onRequestCount}
              {result.onRequestCount > result.onRequest.length
                ? `, showing ${result.onRequest.length}`
                : ''}
            </div>
            <div className="muted" style={{ fontSize: 13, marginBottom: 10, maxWidth: 640 }}>
              These fit the line too. Their vendor does not publish list prices, so they are not
              ranked against the priced ones — an unknown price is not a low one. You can still add
              one to the catalogue and set your own price there.
            </div>
            <CameraTable rows={result.onRequest} isAdmin={isAdmin} onAdopt={adopt} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * One results table, used for both the priced list and the price-on-request one
 * — the only difference is the price cell, which reads "on request" when the
 * vendor publishes no list price. Sharing the table is what makes an unpriced
 * camera adoptable from the same row the priced ones offer.
 */
function CameraTable({
  rows,
  isAdmin,
  onAdopt,
}: {
  rows: Camera[];
  isAdmin: boolean;
  onAdopt: (c: Camera) => void;
}) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table>
        <thead>
          <tr className="muted" style={{ fontSize: 12 }}>
            <th style={cell}>Camera</th>
            <th style={num}>Resolution</th>
            <th style={num}>Speed</th>
            <th style={cell}>Interface</th>
            <th style={num}>mm/px</th>
            <th style={num}>px on defect</th>
            <th style={num}>Margin</th>
            <th style={num}>Price</th>
            <th style={cell} />
          </tr>
        </thead>
        <tbody>
          {rows.map((c) => (
            <tr key={`${c.vendor}-${c.model}`}>
              <td style={cell}>
                <div style={{ fontWeight: 600 }}>{c.model}</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {c.vendor} · {c.family} · {c.colour}
                  {c.sensor ? ` · ${c.sensor}` : ''}
                  {c.ingressProtection ? ` · ${c.ingressProtection}` : ''}
                </div>
              </td>
              <td style={num}>
                {c.widthPx.toLocaleString('nl-NL')}
                {/* px/line belongs to line scan alone. Keying this off a missing
                    height labelled every Basler area-scan camera as line scan,
                    because their part numbers do not encode height — an absent
                    figure read as a different kind of camera. */}
                {c.scan === 'line'
                  ? ' px/line'
                  : c.heightPx
                    ? ` × ${c.heightPx.toLocaleString('nl-NL')}`
                    : ' px'}
              </td>
              <td style={num}>
                {c.speed} {c.speedUnit}
              </td>
              <td style={cell}>
                {c.interface}
                {c.needsFrameGrabber && (
                  <span
                    title="Needs a frame grabber card and a PCIe slot — budget both"
                    style={{ color: 'var(--warn, #f59e0b)', marginLeft: 6 }}
                  >
                    +card
                  </span>
                )}
              </td>
              <td style={num}>{c.mmPerPixel.toFixed(3)}</td>
              <td style={num}>{c.pixelsPerFeature.toFixed(1)}</td>
              <td style={num}>{c.headroom.toFixed(1)}×</td>
              <td style={num}>
                {c.listPrice === null ? (
                  <span className="muted">on request</span>
                ) : (
                  `${c.currency === 'EUR' ? '€' : c.currency} ${c.listPrice.toLocaleString('nl-NL')}`
                )}
                {c.shutterUnverified && (
                  <div
                    className="muted"
                    style={{ fontSize: 11 }}
                    title="The vendor does not publish the shutter type for this model. On a moving belt a rolling shutter skews the part — check the datasheet before ordering."
                  >
                    shutter?
                  </div>
                )}
              </td>
              <td style={cell}>
                {c.inCatalogue ? (
                  <span className="muted" style={{ fontSize: 12 }}>in catalogue</span>
                ) : (
                  isAdmin && (
                    <button className="btn sm" onClick={() => void onAdopt(c)}>
                      Add to catalogue
                    </button>
                  )
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Field({
  label,
  unit,
  value,
  onChange,
  hint,
}: {
  label: string;
  unit: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
}) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: 13, marginBottom: 4 }}>
        {label} <span className="muted">({unit})</span>
      </div>
      <input
        value={value}
        inputMode="decimal"
        onChange={(e) => onChange(e.target.value)}
        style={{ width: 110 }}
      />
      {hint && (
        <div className="muted" style={{ fontSize: 11, marginTop: 3, maxWidth: 130 }}>
          {hint}
        </div>
      )}
    </label>
  );
}

function Check({
  label,
  checked,
  onChange,
  title,
  emphasis,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  title?: string;
  /**
   * A CSS colour that makes this filter stand out, larger and bold. The two
   * filter groups use different colours: the vendor filters take the accent
   * (they pick a brand), the spec filters take the warn tone (they describe the
   * line, not a make), so the eye reads them as two separate kinds of choice.
   */
  emphasis?: string;
}) {
  return (
    <label
      style={{
        display: 'flex',
        gap: 6,
        alignItems: 'center',
        fontSize: emphasis ? 15 : 13,
        fontWeight: emphasis ? 600 : undefined,
        color: emphasis ?? undefined,
      }}
      title={title}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={emphasis ? { accentColor: emphasis, width: 16, height: 16 } : undefined}
      />
      {label}
    </label>
  );
}
